import { AccountRunStatus, AccountSide } from '@hit-game/shared/enums';
import { SIDE_NAMES } from '@hit-game/shared/constants';
import { classifyGroupAction, groupIssues, sideTotal } from '@hit-game/shared/utils';
import type { IAccountDataResponse, IGroup, IHitBulkActionResult } from '@hit-game/shared/types';
import { motion } from 'framer-motion';
import { Check, LogIn, LogOut, Monitor, Pencil, Play, RotateCw, Square } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { groupService } from '../../services/group.service';
import { formatMoney } from '../../utils/format';
import { GAME_BADGES, GAME_LABELS, MEMBER_COLORS } from '../group/group.constants';
import { AccountCard, type AccountCardActions } from './AccountCard';
import { ActionButton } from './ActionButton';

/**
 * Tổng tiền hai phe của cả nhóm: `Phe A: 800.000đ · Phe B: 800.000đ`.
 *
 * Chỉ hiện TỔNG mỗi phe (chi tiết từng cửa xem trong popup sửa nhóm): phe là nhãn A/B được
 * xáo lại mỗi ván cho 2 trong 4 tài khoản, không gắn cứng với cửa nào.
 */
function sideAmountsText(group: IGroup): string {
  const parts = Object.values(AccountSide)
    .map((side) => ({ side, amount: sideTotal(group.sideConfigs, side) }))
    .filter((row) => row.amount > 0)
    .map((row) => `${SIDE_NAMES[row.side]}: ${formatMoney(row.amount)}đ`);

  return parts.length === 0 ? 'Chưa đặt tiền' : parts.join(' · ');
}

const CTRL_LABEL_CLASS =
  'flex items-center text-[9px] font-bold uppercase tracking-wider text-ink-dim';

/** Toast lỗi liệt kê nhiều dòng nên cần lâu hơn mặc định để đọc kịp. */
const BULK_ERROR_TOAST_MS = 9_000;

/**
 * Thao tác cả nhóm đang chạy dở.
 *
 * Một biến chứ không phải mỗi nút một cờ: hai thao tác nhóm chạy chồng nhau thì
 * cái sau đọc trạng thái dở dang của cái trước (Start giữa lúc Stop chưa gỡ xong
 * socket là nối lại đúng cái sắp bị đóng).
 */
type BusyKind = 'login' | 'logout' | 'start' | 'stop' | 'restart';

/**
 * Gộp kết quả thao tác cả nhóm thành MỘT câu đọc được.
 *
 * Server luôn trả 200 kèm mảng kết quả từng tài khoản, kể cả khi hỏng hết — nếu
 * chỉ nhìn mã trạng thái thì bấm Đăng nhập xong thấy toast xanh mà chẳng có gì
 * xảy ra, và không biết tài khoản nào hỏng.
 */
function summarize(results: IHitBulkActionResult[], action: string): { ok: boolean; text: string } {
  const failed = results.filter((item) => !item.success);
  // Thành công nhưng kèm cảnh báo (vd đăng xuất qua proxy chết) — vẫn phải hiện như lỗi, không
  // thì toast xanh "4/4" che mất việc proxy đã chết.
  const warned = results.filter((item) => item.success && item.warning);
  if (failed.length === 0 && warned.length === 0) {
    return {
      ok: true,
      text: `${action}: ${String(results.length)}/${String(results.length)} tài khoản`,
    };
  }

  const detail = [
    ...failed.map((item) => `${item.username}: ${item.error ?? 'lỗi'}`),
    ...warned.map((item) => `⚠ ${item.username}: ${item.warning ?? ''}`),
  ].join('\n');
  return {
    ok: false,
    text: `${action}: ${String(results.length - failed.length)}/${String(results.length)} thành công\n${detail}`,
  };
}

interface GroupCardProps extends AccountCardActions {
  group: IGroup;
  /** Toàn bộ tài khoản của user — thành viên nhóm được tra ra từ đây. */
  accounts: IAccountDataResponse[];
  onChanged: () => void;
  onEdit: (group: IGroup) => void;
  /** Mở bảng "Show Phiên" — các ván nhóm đã đánh, dựng từ lịch sử cược. */
  onShowSessions: (group: IGroup) => void;
  /** Đang ở chế độ chọn nhiều nhóm — hiện ô tick, khoá nút lẻ, click thẻ để chọn. */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (groupId: string) => void;
}

export function GroupCard({
  group,
  accounts,
  onChanged,
  onEdit,
  onShowSessions,
  selectable = false,
  selected = false,
  onToggleSelect,
  ...actions
}: GroupCardProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<BusyKind | null>(null);

  /**
   * Thành viên nhóm kèm PHE của họ.
   *
   * Không còn ghép `side`: phe được random lại mỗi ván nên thẻ không có phe cố
   * định nào để hiện — và từ khi bỏ thanh phiên thì phe của ván đang chạy cũng
   * không còn hiện ở đâu trên giao diện.
   */
  const members = useMemo(() => {
    const byId = new Map(accounts.map((item) => [String(item._id), item]));
    // `flatMap` + mảng rỗng thay cho `map` + `filter` có type predicate: bỏ luôn
    // được `undefined` khỏi kiểu mà không phải tự khai lại shape của phần tử.
    return group.members.flatMap((member) => {
      const account = byId.get(String(member.accountId));
      return account ? [{ account }] : [];
    });
  }, [group.members, accounts]);

  const groupId = String(group._id);
  const running = members.some(({ account }) => account.status === AccountRunStatus.RUNNING);
  // `members` đã BỎ những thành viên không tra được account (bị xoá / chưa load).
  // Buộc số tra được == số thành viên khai trong nhóm, nếu không một member thiếu
  // sẽ bị `every` bỏ qua và thẻ cho Start trong khi `groupEligibility` + server
  // lại skip (member resolve ra `undefined`). Giữ đúng MỘT định nghĩa "đăng nhập đủ".
  const allLoggedIn =
    members.length === group.members.length &&
    members.length > 0 &&
    members.every(({ account }) => account.isLoggedIn);
  /** Còn ÍT NHẤT một phiên để dọn — điều kiện của Đăng xuất, không đối xứng với `allLoggedIn`. */
  const anyLoggedIn = members.some(({ account }) => account.isLoggedIn);
  /**
   * Lý do nhóm chưa chạy được — server TỪ CHỐI đăng nhập nhóm ở trạng thái này.
   *
   * Đọc ĐÚNG hàm mà server dùng, nên chữ trên thẻ khớp câu lỗi server trả về.
   * Tắt nút ngay ở đây để người dùng thấy lý do, thay vì bấm rồi mới nhận lỗi.
   */
  const issues = groupIssues({
    gameType: group.gameType,
    memberCount: group.members.length,
    sideConfigs: group.sideConfigs,
  });

  // Điều kiện Start/Stop đi qua ĐÚNG luật mà bulk dùng (`classifyGroupAction`),
  // để nút trên thẻ và bulk không bao giờ lệch nhau. Login/Logout/Restart không
  // thuộc bulk nên giữ điều kiện riêng bên dưới.
  const eligCtx = { running, allLoggedIn, issues };
  const canStart = classifyGroupAction('start', eligCtx).ok;
  const canStop = classifyGroupAction('stop', eligCtx).ok;

  const selectMode = selectable;

  const runAction = async (
    kind: BusyKind,
    call: () => Promise<{
      results: IHitBulkActionResult[];
      deferred?: boolean;
      sessionLabel?: string | null;
    }>,
    label: string,
  ) => {
    setBusy(kind);
    try {
      const { results, deferred, sessionLabel } = await call();
      // Stop êm: nhóm còn treo tiền ở một ván — chưa dừng, phải nói rõ để khỏi tưởng đã dừng.
      if (deferred) {
        toast.info(
          `Sẽ dừng sau khi ván ${sessionLabel ? `#${sessionLabel} ` : ''}kết thúc — nhóm không đặt ván mới`,
        );
        onChanged();
        return;
      }
      const summary = summarize(results, label);
      if (summary.ok) toast.success(summary.text);
      else toast.error(summary.text, BULK_ERROR_TOAST_MS);
      onChanged();
    } catch {
      // Toast lỗi do interceptor bắn.
    } finally {
      setBusy(null);
    }
  };

  // Stop là tiền thật: bấm nhầm là nhóm lỡ ván kế (có khi đúng ván nổ hũ) — hỏi lại trước khi dừng.
  const stopGroup = async () => {
    const accepted = await confirm({
      title: 'Dừng nhóm',
      message: `Dừng nhóm "${group.name}"? Nếu đang có ván đã đặt cược, nhóm sẽ đợi ván đó kết thúc rồi mới dừng. Từ ván kế tiếp nhóm không đặt cược nữa.`,
      confirmText: 'Stop',
      type: 'danger',
    });
    if (!accepted) return;
    await runAction('stop', () => groupService.stop(groupId), 'Dừng engine');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`bg-panel relative space-y-4 rounded-xl border p-5 shadow-sm transition-shadow ${
        selected ? 'border-accent ring-accent ring-2' : 'border-line'
      }`}
    >
      {/* Chế độ chọn: một lớp phủ trong suốt nuốt mọi click để chọn/bỏ chọn cả
          thẻ, đồng thời khoá các nút lẻ bên dưới — khỏi vừa chọn vừa lỡ bấm Start
          một nhóm. Ô tick nổi lên trên lớp phủ chỉ để nhìn. */}
      {selectMode && (
        <button
          type="button"
          onClick={() => onToggleSelect?.(groupId)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Bỏ chọn' : 'Chọn'} nhóm ${group.name}`}
          className="absolute inset-0 z-20 cursor-pointer rounded-xl"
        />
      )}
      {selectMode && (
        <span
          className={`pointer-events-none absolute right-3 top-3 z-30 grid h-6 w-6 place-items-center rounded-md border-2 transition-colors ${
            selected ? 'border-accent bg-accent text-white' : 'border-line bg-panel'
          }`}
        >
          {selected && <Check className="h-3.5 w-3.5" />}
        </span>
      )}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-ink text-lg font-bold">{group.name}</h3>
            <span
              className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                GAME_BADGES[group.gameType] ?? 'bg-panel-hover text-ink-dim'
              }`}
              title={GAME_LABELS[group.gameType] ?? group.gameType}
            >
              {group.gameType}
            </span>
            {/* Đang chạy vẫn mở: tiền hai phe sửa được và áp dụng từ ván sau. Tên/game/thành
                viên thì khoá trong popup (runner giữ ảnh chụp từ lúc Start, server cũng từ chối). */}
            <button
              type="button"
              onClick={() => {
                onEdit(group);
              }}
              className="text-ink-dim hover:bg-panel-hover hover:text-accent rounded p-1 transition-colors"
              title={
                running
                  ? 'Nhóm đang chạy — sửa được tiền cược, áp dụng từ ván sau'
                  : 'Sửa nhóm — tên, game và cấu hình cược'
              }
            >
              <Pencil size={13} />
            </button>
            <span className="text-ink-dim text-xs font-semibold" title="Tiền cược mỗi phe">
              {sideAmountsText(group)}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs font-medium">
            {members.length > 0 ? (
              members.map(({ account }, index) => (
                <span
                  key={String(account._id)}
                  style={{ color: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
                >
                  {account.username}
                </span>
              ))
            ) : (
              <span className="text-ink-dim">Chưa có thành viên</span>
            )}
          </div>
        </div>

        {/* Lưới 2 cột đều nhau: xếp bằng flex thì bề rộng nút chạy theo độ dài
            chữ, "Đăng nhập" đẩy lệch hẳn so với "Start" ở hàng dưới. */}
        {/* Hai dải 3 nút bằng nhau, cột nhãn cố định 28px — AUTH là phiên với nhà
            cái, CTRL là engine đánh hộ. Gộp một hàng thì "Đăng xuất" và "Stop"
            nhìn như hai cách gọi của cùng một việc, trong khi Stop chỉ dừng đánh
            và giữ nguyên phiên. */}
        <div className="flex shrink-0 flex-col gap-1.5">
          <div className="grid grid-cols-[28px_1fr_1fr_1fr] gap-1">
            <span className={CTRL_LABEL_CLASS}>Auth</span>
            <ActionButton
              onClick={() => {
                void runAction('login', () => groupService.login(groupId), 'Đăng nhập nhóm');
              }}
              // CỐ Ý không phụ thuộc `issues`: đăng nhập chỉ mở phiên với nhà cái,
              // không đặt đồng nào. Nhóm thiếu người hay chưa khai tiền vẫn phải
              // đăng nhập được để còn xem số dư/lịch sử. Bất biến "đủ 4 người, đủ
              // tiền hai phe" do Start canh (`classifyGroupAction` + `startCore`)
              // — đó mới là chỗ tiền thật đi ra.
              disabled={members.length === 0 || allLoggedIn || busy !== null}
              color="blue"
              title={allLoggedIn ? 'Cả nhóm đã đăng nhập' : 'Đăng nhập mọi tài khoản trong nhóm'}
            >
              {busy === 'login' ? (
                <span className="border-accent h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
              ) : (
                <LogIn className="h-3.5 w-3.5" />
              )}
              Đăng nhập
            </ActionButton>
            <ActionButton
              onClick={() => {
                void runAction('logout', () => groupService.logout(groupId), 'Đăng xuất nhóm');
              }}
              // Đang đánh mà đăng xuất là mất phiên giữa ván — mất tiền cược đang treo.
              // `!anyLoggedIn`: không còn phiên nào thì đăng xuất là lệnh rỗng —
              // trước đây nút vẫn bật trong khi Đăng nhập lại tắt, nên trạng thái
              // "vừa đăng xuất xong" hiện ra như thể chỉ đăng xuất được nữa.
              disabled={members.length === 0 || running || busy !== null || !anyLoggedIn}
              title={
                anyLoggedIn
                  ? 'Đăng xuất mọi tài khoản trong nhóm'
                  : 'Không còn phiên nào để đăng xuất'
              }
            >
              {busy === 'logout' ? (
                <span className="border-ink-dim h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
              ) : (
                <LogOut className="h-3.5 w-3.5" />
              )}
              Đăng xuất
            </ActionButton>
            {/* KHÔNG tắt khi nhóm đã dừng: bảng dựng từ lịch sử cược đã đồng bộ,
                và lúc cần soi lại một đêm chạy thì nhóm thường đã tắt từ lâu. */}
            <ActionButton
              onClick={() => {
                onShowSessions(group);
              }}
              color="purple"
            >
              <Monitor className="h-3.5 w-3.5" />
              Show Phiên
            </ActionButton>
          </div>

          <div className="grid grid-cols-[28px_1fr_1fr_1fr] gap-1">
            <span className={CTRL_LABEL_CLASS}>Ctrl</span>
            <ActionButton
              onClick={() => {
                void runAction('start', () => groupService.start(groupId), 'Bật engine');
              }}
              /* Engine đặt lệnh bằng phiên đã đăng nhập — chưa đăng nhập thì
                 server cũng từ chối, tắt sẵn ở đây để thấy lý do trước khi bấm. */
              disabled={!canStart || busy !== null}
              color="green"
              {...(allLoggedIn ? {} : { title: 'Đăng nhập nhóm trước khi bật engine' })}
            >
              {busy === 'start' ? (
                <span className="border-success h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              Start
            </ActionButton>
            <ActionButton
              onClick={() => {
                void stopGroup();
              }}
              disabled={!canStop || busy !== null}
              color="red"
            >
              {busy === 'stop' ? (
                <span className="border-danger h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
              ) : (
                <Square className="h-3.5 w-3.5" />
              )}
              Stop
            </ActionButton>
            <ActionButton
              onClick={() => {
                void runAction(
                  'restart',
                  () => groupService.restart(groupId),
                  'Khởi động lại engine',
                );
              }}
              disabled={!running || busy !== null}
              color="orange"
            >
              {busy === 'restart' ? (
                <span className="border-warning h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
              ) : (
                <RotateCw className="h-3.5 w-3.5" />
              )}
              Restart
            </ActionButton>
          </div>
        </div>
      </div>

      {members.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {members.map(({ account }, index) => (
            <AccountCard
              key={String(account._id)}
              account={account}
              inGroup
              headerColor={MEMBER_COLORS[index % MEMBER_COLORS.length]}
              {...actions}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}
